<template>
  <div :id="navigationId" ref="ingredients" class="oap-product-ingredients">
    <div
      :class="[
        'oap-product-ingredients__content',
        `oap-product-ingredients__content--layout-${layout}`,
      ]"
    >
      <video
        v-if="items[activeItemIndex].video"
        class="oap-product-ingredients__video"
        preload="metadata"
        :src="videoSource"
        :autoplay="items[activeItemIndex].video.autoplay"
        playsinline
        muted
        loop
        aria-hidden="true"
      ></video>
      <picture v-if="items[activeItemIndex].image" class="oap-product-ingredients__image">
        <source media="(min-width: 650px)" :srcset="imageSource.lg" />
        <source :srcset="imageSource.sm" />
        <img
          class="lazyload"
          :alt="isMobile.value ? imageSource.sma : imageSource.lga"
          src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
          :data-src="imageSource.sm"
          aria-hidden="true"
        />
      </picture>
      <slot></slot>
    </div>
    <div class="oap-product-ingredients__footer">
      <div v-if="items.length > 1" class="oap-product-ingredients__controls">
        <button :disabled="isPrevDisabled" :aria-label="previousButtonAriaLabel" @click="prevItem">
          <svg class="icon">
            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-chevron" />
          </svg>
        </button>
        <button :disabled="isNextDisabled" :aria-label="nextButtonAriaLabel" @click="nextItem">
          <svg class="icon">
            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-chevron" />
          </svg>
        </button>
      </div>
      <slot name="link-for-all"></slot>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { AnalyticsHandler, intersectionViewportObserver } from '@Foundation';

const INNER_INDENT = {
  sm: 40,
  lg: 32,
};

const INGREDIENTS_CARD_SELECTOR = '.oap-product-ingredients__card';
const INGREDIENTS_CARD_SELECTOR_ACTIVE = 'oap-product-ingredients__card--active';
const INGREDIENTS_CARD_SELECTOR_OPEN = 'oap-product-ingredients__card--open';

const MOBILE_SCREEN_END = 650;

const props = defineProps({
  items: { type: Array, default: () => [] },
  swipeDistanceToSlide: { type: Number, default: 60 },
  analytics: { type: Object, default: () => {} },
  navigationId: { type: String, default: '' },
  previousButtonAriaLabel: { type: String, default: 'Previous' },
  nextButtonAriaLabel: { type: String, default: 'Next ingredient' },
});

const isMobile = computed(() => {
  return window.innerWidth < MOBILE_SCREEN_END;
});

const isPrevDisabled = computed(() => {
  return activeItemIndex.value === 0;
});

const isNextDisabled = computed(() => {
  return activeItemIndex.value === props.items.length - 1;
});

const imageSource = computed(() => {
  return props.items[activeItemIndex.value].image;
});

const videoSource = computed(() => {
  if (!isInViewport.value) return;

  return isMobile.value
    ? props.items[activeItemIndex.value].video.src.sm
    : props.items[activeItemIndex.value].video.src.lg;
});

const layout = computed(() => {
  switch (props.items.length) {
    case 1:
      return 'one';
    case 2:
      return 'two';
    case 3:
      return 'three';
    default:
      return 'default';
  }
});

const activeItemIndex = ref(0);

let cards = null;

const innerBlock = ref(null);
const ingredients = ref(null);

let isInViewport = ref(false);

let dragDistance = 0;

function setActiveCard() {
  cards.forEach((card) => card.classList.remove(INGREDIENTS_CARD_SELECTOR_ACTIVE));
  cards[activeItemIndex.value].classList.add(INGREDIENTS_CARD_SELECTOR_ACTIVE);
}

function pushAnalytics() {
  if (props.analytics) {
    try {
      AnalyticsHandler.getAnalyticsHandler().push(props.analytics);
    } catch (e) {
      /* istanbul ignore next */
      console.warn('Could not push to dataLayer', e);
    }
  }
}

function prevItem() {
  if (activeItemIndex.value > 0) {
    activeItemIndex.value--;

    setActiveCard();
    moveItem(activeItemIndex.value);
  }

  pushAnalytics();
}

function nextItem() {
  if (activeItemIndex.value !== props.items.length - 1) {
    activeItemIndex.value++;

    setActiveCard();
    moveItem(activeItemIndex.value);
  }

  pushAnalytics();
}

function moveItem(index) {
  expandCard(false, index);

  if (isMobile.value) {
    expandCard(true, index);

    innerBlock.value.scrollTo({
      left: cards[index].offsetLeft - INNER_INDENT.sm,
      behavior: 'smooth',
    });
  } else {
    innerBlock.value.scrollTo({
      top:
        cards[index].offsetTop - cards[index - 1]?.getBoundingClientRect().height - INNER_INDENT.lg,
      behavior: 'smooth',
    });
  }
}

function expandCard(expand, index) {
  cards.forEach((card) => card.classList.remove(INGREDIENTS_CARD_SELECTOR_OPEN));
  cards[index].classList.toggle(INGREDIENTS_CARD_SELECTOR_OPEN, expand);
}

function handleTouchStart(e) {
  e.preventDefault();

  dragDistance = 0;

  if (isMobile.value) {
    dragDistance = e.touches ? e.touches[0].pageX : e.pageX;
  } else {
    dragDistance = e.touches ? e.touches[0].pageY : e.pageY;
  }
}

function handleTouchEnd(e) {
  e.preventDefault();

  if (isMobile.value) {
    dragDistance = dragDistance - (e.changedTouches ? e.changedTouches[0].pageX : e.pageX);
  } else {
    dragDistance = dragDistance - (e.changedTouches ? e.changedTouches[0].pageY : e.pageY);
  }

  if (!dragDistance) {
    if (e.target.closest('button')) {
      e.target.closest('button').click();
    }
    if (e.target.closest('a')) {
      e.target.closest('a').click();
    }
  }

  handleTouchMove(e, dragDistance);
}

function handleTouchMove(e, dragValue) {
  if (dragValue < 0 && Math.abs(dragValue) >= props.swipeDistanceToSlide) {
    prevItem();
  } else if (dragValue > 0 && Math.abs(dragValue) >= props.swipeDistanceToSlide) {
    nextItem();
  }
}

onMounted(() => {
  innerBlock.value = ingredients.value.querySelector('.oap-product-ingredients__inner');
  cards = innerBlock.value.querySelectorAll(INGREDIENTS_CARD_SELECTOR);

  setActiveCard();

  cards.forEach((card, index) => {
    const button = card.querySelector('button');
    if (button) {
      button.addEventListener('click', () => {
        expandCard(activeItemIndex.value !== index, index);
      });
    }
  });

  if (isMobile.value && activeItemIndex.value === 0) {
    expandCard(true, 0);
  }

  intersectionViewportObserver(ingredients.value, {
    checkIsVisible: false,
    threshold: [0.006],
  }).then(() => {
    isInViewport.value = true;
  });

  if ('ontouchstart' in window) {
    innerBlock.value.addEventListener('touchstart', handleTouchStart);
    innerBlock.value.addEventListener('touchend', handleTouchEnd);
  }
});
</script>
